<template>
    <div>
        <ConfirmDialog
            :is-open="deleteCarReservationDialog"
            :data="carReservation"
            @on-confirm="removeCarReservation(carReservation)"
            @on-abort="closeDeleteCarReservationDialog"
        >
            <template #title>
                Usuwanie rezerwacji
            </template>
            <template #content>
                Czy na pewno chcesz usunąć rezerwację?
            </template>
            <template #confirmButton>
                Usuń
            </template>
        </ConfirmDialog>

        <v-dialog
            :value="isOpen"
            max-width="500px"
            transition="slide-y-transition"
            eager
            @keydown.esc="onClose"
            @click:outside="onClose"
        >
            <v-card>
                <v-card-title>
                    <h1 class="text-subtitle-1">
                        <template v-if="reservationData.actionName === 'create'">
                            Tworzenie rezerwacji: <strong>{{ reservationDate }}</strong>
                        </template>
                        <template v-else>
                            Edycja rezerwacji: <strong>{{ reservationDate }}</strong>
                        </template>
                    </h1>
                </v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col cols="12">
                            <v-text-field
                                v-model="carReservation.name"
                                label="Nazwa rezerwacji"
                                :error-messages="carNameErrors"
                                :disabled="reservationData.actionName === 'update'"
                                outlined
                                dense
                                required
                            />
                        </v-col>
                        <v-col v-if="reservationData.actionName === 'update'" cols="12">
                            <v-text-field
                                :value="userNameHandler(carReservation.createdBy)"
                                label="Rezerwujący"
                                :disabled="reservationData.actionName === 'update'"
                                outlined
                                dense
                                required
                            />
                        </v-col>
                        <v-col cols="12">
                            <v-select
                                v-model="carReservation.carTypeId"
                                label="Samochód"
                                :items="carReservationsTypes"
                                item-value="id"
                                item-text="title"
                                no-data-text="Nie znaleziono typu samochodu"
                                :error-messages="carTypeIdErrors"
                                :disabled="reservationData.actionName === 'update'"
                                outlined
                                dense
                                required
                            />
                        </v-col>
                        <v-col v-if="reservationData.actionName === 'update'" cols="12">
                            <v-select
                                v-model="carReservation.carReservationStatusId"
                                label="Status"
                                :items="carReservationsStatuses"
                                item-value="id"
                                item-text="name"
                                no-data-text="Nie znaleziono statusu"
                                outlined
                                dense
                                required
                            />
                        </v-col>
                        <v-col v-if="reservationData.actionName !== 'update'" cols="12">
                            <v-slider
                                v-model="defaultTime"
                                label="Długość rezerwacji"
                                thumb-label="always"
                                hide-details
                                ticks
                                always-dirty
                                :disabled="carReservation.allDay"
                                :min="30"
                                :max="maxTime"
                                :step="30"
                            />
                        </v-col>
                        <v-col v-if="reservationData.actionName !== 'update'" cols="12">
                            <v-checkbox
                                v-model="carReservation.allDay"
                                label="Rezerwacja na cały dzień"
                            />
                        </v-col>
                        <v-col cols="12">
                            <v-autocomplete
                                v-model="carReservation.assignPerson"
                                label="Osoby przypisane"
                                :items="users"
                                :filter="customUsersFilter"
                                :loading="usersSearchLoading"
                                :search-input.sync="usersSearch"
                                class="v-text-field--placeholder-dark"
                                required
                                dense
                                multiple
                                chips
                                outlined
                                deletable-chips
                                cache-items
                                item-value="id"
                                no-data-text="Nie znaleziono"
                                clearable
                                @change="clearUserSearch()"
                            >
                                <template slot="no-data">
                                    <v-list-item>
                                        <v-list-item-title>
                                            <template v-if="!usersSearch">
                                                Wpisz treść aby odnaleźć <strong>użytkownika</strong>.
                                            </template>
                                            <template v-else-if="usersSearchLoading">
                                                Trwa wyszukiwanie...
                                            </template>
                                            <template v-else>
                                                Nie znaleziono
                                            </template>
                                        </v-list-item-title>
                                    </v-list-item>
                                </template>
                                <template slot="selection" slot-scope="data">
                                    <template v-if="data.item.firstName && data.item.lastName">
                                        <v-chip
                                            close
                                            @update:active="removeUser(data.item)"
                                        >
                                            {{ data.item.firstName }} {{ data.item.lastName }}
                                        </v-chip>
                                    </template>
                                    <template v-else>
                                        <v-chip
                                            close
                                            @update:active="removeUser(data.item)"
                                        >
                                            {{ data.item.username }}
                                        </v-chip>
                                    </template>
                                </template>
                                <template slot="item" slot-scope="data">
                                    <template v-if="data.item.firstName && data.item.lastName">
                                        {{ data.item.firstName }} {{ data.item.lastName }}
                                    </template>
                                    <template v-else>
                                        {{ data.item.username }}
                                    </template>
                                </template>
                            </v-autocomplete>
                        </v-col>
                    </v-row>
                </v-card-text>

                <v-divider />

                <v-card-actions>
                    <v-btn
                        :color="reservationData.actionName === 'create' ? 'error' : 'info'"
                        text
                        data-sentry-label="Anuluj"
                        :disabled="actionLoading"
                        @click="onClose()"
                    >
                        Anuluj
                    </v-btn>
                    <v-spacer />
                    <v-btn
                        v-if="reservationData.actionName === 'update' && canDeleteCarReservation"
                        color="error"
                        text
                        data-sentry-label="Usuń"
                        :loading="actionLoading"
                        @click="deleteCarReservation()"
                    >
                        Usuń
                    </v-btn>
                    <v-btn
                        color="success"
                        text
                        :loading="actionLoading"
                        data-sentry-label="Zapisz"
                        @click="saveCarReservation()"
                    >
                        Zapisz
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import ConfirmDialog from '@/components/common/ConfirmDialog'

import customFilters from '@/mixins/customFilters'
import filters from '@/mixins/customFilters'

import { errorHandler, addSentryBreadcrumb } from '@/utils/errorHandler'
import { hasUserPermission } from '@/utils/permissionsHandler'

import { required, minLength } from 'vuelidate/lib/validators'
import { addMinutes, setHours, setMinutes } from 'date-fns'
import { format } from 'date-fns'

let searchTimer

export default {
    name: 'CarReservationDialog',
    components: {
        ConfirmDialog
    },
    mixins: [
        filters, customFilters
    ],
    props: {
        isOpen: {
            type: Boolean
        },
        reservationData: {
            type: Object,
            default: null
        },
    },
    data() {
        return {
            carReservation: {
                name: null,
                carTypeId: null,
                startDate: null,
                endDate: null,
                allDay: false,
                assignPerson: [],
            },
            pageTitle: '',
            maxTime: 480,
            defaultTime: 30,
            actionLoading: false,
            deleteCarReservationDialog: false,
            reservationDate: null,
            usersSearch: null,
            usersSearchLoading: false,
            users: [],
        }
    },
    validations: {
        carReservation: {
            name: {
                minLength: minLength(2),
                required
            },
            carTypeId: {
                required
            },
        }
    },
    computed: {
        carReservationsStatuses() {
            return this.$store.state.administration.carReservations.carReservationsStatuses
        },
        carReservationsTypes() {
            return this.$store.state.administration.carReservations.carReservationsTypes
        },
        canEditCarReservation() {
            return hasUserPermission('car_reservations_update')
        },
        canDeleteCarReservation() {
            return hasUserPermission('car_reservations_delete')
        },
        // Validations
        carNameErrors() {
            const errors = []

            if (!this.$v.carReservation.name.$dirty) return errors
            if (!this.$v.carReservation.name.minLength)
                errors.push('Nazwa rezerwacji musi zawierać minimum 2 znaki.')
            if (!this.$v.carReservation.name.required)
                errors.push('Nazwa rezerwacji jest wymagana.')

            return errors
        },
        carTypeIdErrors() {
            const errors = []

            if (!this.$v.carReservation.carTypeId.$dirty) return errors
            if (!this.$v.carReservation.carTypeId.required)
                errors.push('Samochód jest wymagany.')

            return errors
        },
    },
    watch: {
        reservationData: {
            handler: function(newValue) {
                if (newValue.info === null) return

                if (newValue.actionName === 'create') {
                    this.carReservation.startDate = newValue.info.dateStr
                    this.reservationDate = `${format(new Date(newValue.info.dateStr), 'YYYY-MM-DD')},  ${format(new Date(newValue.info.dateStr), 'HH:mm')}`
                    this.carReservation.carTypeId = parseInt(newValue.info.resource.id)

                    return
                } else {
                    this.carReservation = newValue.reservationData
                    this.users = newValue.reservationData.assignPersonRead
                    this.reservationDate = `${format(new Date(newValue.info.event.extendedProps.startDate), 'YYYY-MM-DD')}, ${format(new Date(newValue.info.event.start), 'HH:mm')} - ${format(new Date(newValue.info.event.end), 'HH:mm')}`
                }
            },
        },
        'carReservation.allDay': function() {
            if (this.carReservation.allDay) {
                this.carReservation.startDate = setHours(new Date(this.carReservation.startDate), 8)
                this.carReservation.startDate = setMinutes(new Date(this.carReservation.startDate), 0)
                this.carReservation.endDate = setHours(new Date(this.carReservation.startDate), 16)
                this.carReservation.endDate = setMinutes(new Date(this.carReservation.endDate), 0)
            }
        },
        usersSearch(value = null) {
            if (value === null) return

            this.usersSearchLoading = true

            clearTimeout(searchTimer)

            searchTimer = setTimeout(() => {
                if (value) {
                    this.searchUsers(value)
                } else {
                    this.usersSearchLoading = false
                    this.users = []
                }
            }, 500)
        }
    },
    methods: {
        clearUserSearch() {
            return this.usersSearch = null
        },
        removeUser(user) {
            const index = this.carReservation.assignPerson.indexOf(user.id)

            this.carReservation.assignPerson.splice(index, 1)
        },
        saveCarReservation() {
            this.$v.$touch()
            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }
            if (this.reservationData.actionName === 'create') {
                this.actionLoading = true
                if (!this.carReservation.allDay) {
                    this.carReservation.endDate = addMinutes(new Date(this.carReservation.startDate), this.defaultTime)
                }
                this.$store.dispatch('administration/saveCarReservation', this.carReservation)
                    .catch(error => {
                        errorHandler(error)
                    })
                    .finally(() => {
                        this.onClose()
                    })
            }
            if (this.reservationData.actionName === 'update') {
                this.actionLoading = true
                this.$store.dispatch('administration/updateCarReservation', this.carReservation)
                    .catch(error => {
                        errorHandler(error)
                    })
                    .finally(() => {
                        this.onClose()
                    })
            }
        },
        deleteCarReservation() {
            this.deleteCarReservationDialog = true
        },
        closeDeleteCarReservationDialog() {
            this.deleteCarReservationDialog = false
        },
        removeCarReservation() {
            this.actionLoading = true
            this.$store.dispatch('administration/deleteCarReservation', this.carReservation )
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.closeDeleteCarReservationDialog()
                    this.onClose()
                })
        },
        onClose() {
            this.actionLoading = false
            this.$emit('on-close')
            this.$v.$reset()
            setTimeout(() => {
                this.carReservation = {},
                this.defaultTime = 30
            }, 500)
        },
        searchUsers(query = '') {
            addSentryBreadcrumb('ui.action', 'Wyszukiwanie użytkownika')

            this.$store.dispatch('administration/findUsers', {
                paginate: '?limit=20',
                query: query
            })
                .then(response => {
                    this.users = response.data.results
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.usersSearchLoading = false
                })
        },
        userNameHandler(user) {
            if (user.firstName && user.lastName) {
                return `${user.firstName} ${user.lastName}`
            } else {
                return user.username
            }
        }
    }
}
</script>

